import React, { FC, ReactElement, useMemo, useRef, useState } from "react";
import { Grid } from "@getregistered/greg-editors";
import { DeleteModal } from "@getregistered/greg-editors/dist/lib/GregGrid/PaginatedGridPrimer/DeleteModal";
import { UpdateStatusModal } from "@getregistered/greg-editors/dist/lib/GregGrid/PaginatedGridPrimer/UpdateStatusModal";
import { gridLoader, nestedFilterParams, NestedFilterParams } from "../../domain/grid/resultLoader";
import {
  AsyncDownloadLink,
  BulkActionLink,
  DownloadLink,
  PaginatedApplicationPrimerGrid,
  SelectionStatePayload,
} from "./PaginatedApplicationPrimerGrid";
import { DEFAULT_PER_PAGE, DEFAULT_PER_PAGE_STORAGE_KEY } from "./constants";
import { RowEditComponent } from "@getregistered/greg-editors/dist/lib/domain/grid/types";
import type { CellRenderFunction, ViewMode, ToolbarAction } from "@getregistered/greg-editors/dist/lib/GregGrid/PaginatedGridPrimer/types";
import type { MenuActionHandler } from "@getregistered/greg-editors/dist/lib/GregGrid/primer-ui/cells/buttonActionProcessor";
import type { GridRow, GridColumn } from "@getregistered/greg-editors/dist/lib/domain/grid/types";
import { BulkActionDefinition, BulkOperationPayload } from "../BulkActions/types";
import { startBulkOperation, pollBulkOperation } from "../BulkActions/bulkOperationFlow";
import { SelectionParams, startExport, pollExportStatus } from "../DownloadTray/downloadFlow";
import { usePreflight } from "../BulkActions/usePreflight";
import {
  buildStatusContentMap,
  STATUS_OPTIONS,
} from "../BulkActions/RegistrationStatusContent";
import { DeleteContent } from "../BulkActions/DeleteContent";
import { InvitationDeleteContent } from "../BulkActions/InvitationDeleteContent";
import { InvitationSendModal } from "../BulkActions/InvitationSendModal";
import { ResendConfirmationModal } from "../BulkActions/ResendConfirmationModal";
import { RestoreConfirmationModal } from "../BulkActions/RestoreConfirmationModal";
import { ArchiveConfirmationModal } from "../BulkActions/ArchiveConfirmationModal";
import {
  archivedAnything,
  bulkArchiveEvents,
  BulkArchiveResult,
  createArchiveRequestTracker,
} from "../BulkActions/bulkArchiveFlow";
import "../BulkActions/BulkActionDialogOverrides.css";

interface GridWithBulkActions extends Grid {
  bulkActions?: BulkActionDefinition[];
}

export interface ReadOnlyGridProps {
  grid: GridWithBulkActions;
  resultsUrl: string;
  // Optional: card-only grids (events, event requests) have no totals row, so
  // they omit this. The total loaders throw clearly if invoked without it.
  totalsUrl?: string;
  downloadLinks?: DownloadLink[];
  eventId?: string;
  gridType?: string;
  reportExportsUrl?: string;
  extraParams?: Record<string, unknown>;
  RowEditor?: RowEditComponent;
  actionCellRenderer?: 'menu' | 'buttons';
  showActions?: boolean;
  showMetaCell?: boolean;
  renderCell?: CellRenderFunction;
  defaultPerPage?: number;
  onSaveCell?: (row: GridRow, column: GridColumn, value?: string) => Promise<void> | void;
  onMenuAction?: MenuActionHandler;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  renderCard?: (row: GridRow, columns: GridColumn[]) => ReactElement;
  leadingCard?: ReactElement;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  toolbarActions?: ToolbarAction[];
  // When true, pin the grid to card view and hide the card/list toggle. The
  // toggle is greg-editors-side and only renders when both renderCard AND
  // onViewModeChange are present, so we force "card" and drop the handler.
  cardOnly?: boolean;
  bulkOperationsUrl?: string;
  resourceType?: string;
  bulkActionsEnabled?: boolean;
  // GR-3342: the events index archives inline against this URL instead of
  // starting a BulkOperation. See bulkArchiveFlow for why there is no
  // operation record to poll.
  bulkArchiveUrl?: string;
}

// Maps the frontend status dropdown value to the backend bulk action key
const STATUS_TO_ACTION_KEY: Record<string, string> = {
  confirmed: "confirm",
  cancelled: "cancel",
};

export const ReadOnlyPrimerGrid: FC<ReadOnlyGridProps> = ({
  grid,
  resultsUrl,
  totalsUrl,
  downloadLinks,
  eventId,
  gridType,
  reportExportsUrl,
  extraParams,
  RowEditor,
  actionCellRenderer = 'menu',
  showActions = false,
  showMetaCell = false,
  renderCell,
  defaultPerPage,
  onSaveCell,
  onMenuAction,
  viewMode,
  onViewModeChange,
  renderCard,
  leadingCard,
  onSearch,
  searchPlaceholder,
  toolbarActions,
  cardOnly = false,
  bulkOperationsUrl,
  resourceType,
  bulkActionsEnabled = false,
  bulkArchiveUrl,
}) => {
  const bulkActions = grid.bulkActions;
  // bulkActionsEnabled is the organisation flag on the asynchronous
  // bulk-operation pipeline, so it gates only the actions that use it. The
  // inline archive path has its own gate: the server omits grid.bulkActions
  // entirely unless the operator holds events:archive (GR-3342).
  const hasBulkActions = !!(
    bulkActions &&
    bulkActions.length > 0 &&
    ((bulkActionsEnabled && bulkOperationsUrl) || bulkArchiveUrl)
  );

  // Modal state
  const [selectionState, setSelectionState] = useState<SelectionStatePayload | null>(null);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isResendConfirmationOpen, setIsResendConfirmationOpen] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [archiveResult, setArchiveResult] = useState<BulkArchiveResult | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  // GR-3342 second review: mirrors isArchiveOpen for the completion handler,
  // which runs after an await and would otherwise read a stale closure. If the
  // dialog was closed while the request was in flight, the report has nowhere
  // to land but the tab counters are still stale, so the reload must still
  // happen.
  const archiveDialogOpenRef = useRef(false);
  // Signalflare follow-up: one generation per opening of the dialog, so a
  // report from before a close-and-reopen is recognised as stale rather than
  // shown in the new dialog as if it were that selection's outcome.
  const archiveRequests = useRef(createArchiveRequestTracker());
  // Whether the grid currently has a selection. Only used to decide whether the
  // Actions menu should exist at all (see showBulkActionMenu).
  const [hasSelection, setHasSelection] = useState(false);
  const [restoreSendEmail, setRestoreSendEmail] = useState(true);
  const [emailOption, setEmailOption] = useState("unconfirmed_only");
  const [deleteConfirmValue, setDeleteConfirmValue] = useState("");
  // Bumped when a bulk operation settles; part of the results query key, so
  // incrementing it refetches the current page (GR-2202: rows kept their
  // pre-operation status until a manual refresh).
  const [resultsCacheKey, setResultsCacheKey] = useState(0);
  // The filters the grid last fetched with, in the nested shape the server
  // parses. SelectionStatePayload.filterParams is NOT used for selections:
  // its flat record both loses repeated multi-choice values and arrives
  // server-side as literal "f[col][]" strings that apply no filter at all —
  // which ran a filtered Select All against the entire event (GR-2206).
  const activeFilterParamsRef = useRef<NestedFilterParams>({});

  const selectionCount = selectionState
    ? selectionState.mode === "explicit"
      ? selectionState.selectedIds.length
      : selectionState.totalCount - selectionState.excludedIds.length
    : 0;

  // Preflight: fetch status distribution when a modal is open
  const isModalOpen =
    isUpdateStatusOpen || isDeleteOpen || isResendConfirmationOpen || isSendOpen || isRestoreOpen;
  const preflightParams = useMemo(() => {
    if (!selectionState) {
      return {
        resource_type: resourceType || "Registration",
        selection_mode: "all_matching" as const,
      };
    }
    if (selectionState.mode === "explicit") {
      return {
        resource_type: resourceType || "Registration",
        selection_mode: "explicit" as const,
        record_ids: selectionState.selectedIds,
      };
    }
    return {
      resource_type: resourceType || "Registration",
      selection_mode: "all_matching" as const,
      excluded_ids: selectionState.excludedIds,
      filter_params: activeFilterParamsRef.current,
    };
  }, [resourceType, selectionState]);

  const { data: preflight, loading: preflightLoading } = usePreflight({
    bulkOperationsUrl: bulkOperationsUrl || "",
    params: preflightParams,
    enabled: isModalOpen && !!bulkOperationsUrl,
  });

  const loader = useMemo(() => {
    const base = gridLoader(resultsUrl, totalsUrl, grid.columns);
    return {
      ...base,
      loadRecords: (options: Parameters<typeof base.loadRecords>[0], signal: AbortSignal) => {
        activeFilterParamsRef.current = nestedFilterParams(grid.columns, options.filters);
        return base.loadRecords(options, signal);
      },
    };
  }, [resultsUrl, totalsUrl, grid.columns]);

  const asyncDownloadLinks: AsyncDownloadLink[] | undefined =
    eventId && gridType && reportExportsUrl
      ? [
          { title: "Download CSV", eventId, gridType, exportType: "csv" as const, reportExportsUrl, extraParams },
          { title: "Download XLSX", eventId, gridType, exportType: "xlsx" as const, reportExportsUrl, extraParams },
        ]
      : undefined;

  const executeAction = async (
    actionKey: string,
    actionParams?: Record<string, unknown>,
    explicitState?: SelectionStatePayload,
  ) => {
    const state = explicitState || selectionState;
    if (!bulkOperationsUrl || !state) return;

    const bulkOp: BulkOperationPayload["bulk_operation"] = {
      action_key: actionKey,
      resource_type: resourceType || "Registration",
      ...(state.mode === "explicit"
        ? { selection_mode: "explicit" as const, record_ids: state.selectedIds }
        : { selection_mode: "all_matching" as const, excluded_ids: state.excludedIds, filter_params: activeFilterParamsRef.current }),
    };

    if (actionParams) {
      bulkOp.action_params = actionParams;
    }

    const operation = await startBulkOperation(bulkOperationsUrl, { bulk_operation: bulkOp });
    if (operation) {
      pollBulkOperation(bulkOperationsUrl, operation.id, () =>
        setResultsCacheKey((key) => key + 1)
      );
    }
  };

  // Inline, so the answer comes back in the same request. The dialog stays
  // open on the report: a partly permitted or partly already-archived
  // selection is the normal case, and closing on it would drop the only
  // account of what did not happen.
  const handleArchive = async () => {
    if (!bulkArchiveUrl || !selectionState) return;

    const generation = archiveRequests.current.current();
    setIsArchiving(true);
    const result = await bulkArchiveEvents(bulkArchiveUrl, selectionState, activeFilterParamsRef.current);
    setResultsCacheKey((key) => key + 1);
    if (!archiveDialogOpenRef.current || !archiveRequests.current.isCurrent(generation)) {
      // Closed mid-flight, or closed and reopened for another selection:
      // nobody will read this report, so do what Done would have done once it
      // was read. Leave isArchiving alone, it may belong to the newer request.
      if (archivedAnything(result)) window.location.reload();
      return;
    }
    setIsArchiving(false);
    setArchiveResult(result);
  };

  const closeArchive = () => {
    archiveDialogOpenRef.current = false;
    setIsArchiveOpen(false);
    // The status tab counters and the Archived tab are server rendered, so a
    // client-side refetch leaves them claiming the rows are still live. Reload
    // once the operator has read the report, but only when something actually
    // moved.
    const archived = archiveResult !== null && archivedAnything(archiveResult);
    setArchiveResult(null);
    if (archived) window.location.reload();
  };

  const handleSelectionDownload = async (exportType: "csv" | "xlsx", state: SelectionStatePayload) => {
    if (!reportExportsUrl || !gridType) return;

    const selectionParams: SelectionParams = state.mode === "explicit"
      ? { selection_mode: "explicit", record_ids: state.selectedIds }
      : { selection_mode: "all_matching", excluded_ids: state.excludedIds, filter_params: activeFilterParamsRef.current };

    const result = await startExport(
      reportExportsUrl,
      gridType,
      exportType,
      {},
      undefined,
      undefined,
      selectionParams
    );

    if (result?.id) {
      pollExportStatus(reportExportsUrl, result.id, exportType, gridType);
    }
  };

  // Build bulkActionLinks from backend action definitions.
  // Combines confirm+cancel into "Update Status", keeps delete separate.
  // Adds selection-aware download links when export config is available.
  const bulkActionLinks: BulkActionLink[] | undefined = useMemo(() => {
    if (!hasBulkActions) return undefined;

    const hasConfirmOrCancel = bulkActions!.some(
      (a) => a.key === "confirm" || a.key === "cancel"
    );
    const hasResendConfirmation = bulkActions!.some((a) => a.key === "resend_confirmation");
    const hasSend = bulkActions!.some((a) => a.key === "send");
    const hasRestore = bulkActions!.some((a) => a.key === "restore");
    const hasDelete = bulkActions!.some((a) => a.key === "delete");
    const hasArchive = !!(bulkArchiveUrl && bulkActions!.some((a) => a.key === "archive"));
    const hasDownloads = !!(reportExportsUrl && gridType);

    const links: BulkActionLink[] = [];

    if (hasConfirmOrCancel) {
      links.push({
        title: "Update Status",
        onClick: (state) => {
          setSelectionState(state);
          setIsUpdateStatusOpen(true);
        },
      });
    }

    if (hasSend) {
      links.push({
        title: "Send / Resend",
        onClick: (state) => {
          setSelectionState(state);
          setIsSendOpen(true);
        },
      });
    }

    if (hasResendConfirmation) {
      links.push({
        title: "Resend Confirmation Email",
        onClick: (state) => {
          setSelectionState(state);
          setIsResendConfirmationOpen(true);
        },
      });
    }

    if (hasRestore) {
      links.push({
        title: "Restore",
        onClick: (state) => {
          setSelectionState(state);
          setIsRestoreOpen(true);
        },
      });
    }

    if (hasDownloads) {
      links.push({
        title: "Download CSV",
        onClick: (state) => handleSelectionDownload("csv", state),
      });
      links.push({
        title: "Download XLSX",
        onClick: (state) => handleSelectionDownload("xlsx", state),
      });
    }

    if (hasArchive) {
      links.push({
        title: "Archive",
        onClick: (state) => {
          setSelectionState(state);
          setArchiveResult(null);
          setIsArchiving(false);
          archiveDialogOpenRef.current = true;
          archiveRequests.current.open();
          setIsArchiveOpen(true);
        },
        dividerBefore: true,
        variant: "danger",
      });
    }

    if (hasDelete) {
      links.push({
        title: "Delete",
        onClick: (state) => {
          setSelectionState(state);
          setIsDeleteOpen(true);
        },
        dividerBefore: true,
        variant: "danger",
      });
    }

    return links.length > 0 ? links : undefined;
  }, [hasBulkActions, bulkActions, reportExportsUrl, gridType, bulkArchiveUrl]);

  const handleUpdateStatus = (status: string) => {
    const actionKey = STATUS_TO_ACTION_KEY[status] || status;
    const actionParams: Record<string, unknown> = {};
    if (emailOption !== "none") {
      actionParams.send_email = emailOption;
    }
    executeAction(actionKey, Object.keys(actionParams).length > 0 ? actionParams : undefined);
    setIsUpdateStatusOpen(false);
    setEmailOption("unconfirmed_only");
  };

  const handleDelete = () => {
    executeAction("delete");
    setIsDeleteOpen(false);
    setDeleteConfirmValue("");
  };

  const handleResendConfirmation = () => {
    executeAction("resend_confirmation");
    setIsResendConfirmationOpen(false);
  };

  const handleSend = () => {
    executeAction("send");
    setIsSendOpen(false);
  };

  // Invitation delete has no status guard — every selected invitation is
  // deletable, matching the single-row action — whereas RegistrationDeleteExecutor
  // only acts on cancelled rows. So the deletable count is resource-specific.
  const isInvitation = resourceType === "Invitation";
  const preflightTotal = preflight?.counts
    ? Object.values(preflight.counts).reduce((total, n) => total + n, 0)
    : undefined;

  const handleRestore = () => {
    executeAction("restore", restoreSendEmail ? { send_email: true } : undefined);
    setIsRestoreOpen(false);
  };

  // The confirm text tracks what will actually be deleted, not the selection —
  // otherwise a mixed selection says "delete 2 attendees" while demanding the
  // user type "Delete 5" (GR-2164 review). Same fallback as the modal copy:
  // until preflight resolves, the selection count is the best estimate.
  const deletableCount = isInvitation
    ? preflightTotal
    : (preflight?.counts ? (preflight.counts.cancelled ?? 0) : undefined);
  const requiredDeleteConfirmText = `Delete ${deletableCount ?? selectionCount}`;
  const isDeleteConfirmValid = deleteConfirmValue.trim() === requiredDeleteConfirmText;
  // When nothing is deletable (registrations: no cancelled rows; invitations:
  // empty selection), keep the button disabled instead of launching an
  // operation that skips every row (GR-2164).
  const nothingDeletable = deletableCount === 0;

  return (
    <>
      <PaginatedApplicationPrimerGrid
        grid={grid}
        loader={loader}
        resultsCacheKey={resultsCacheKey}
        defaultPerPage={defaultPerPage || DEFAULT_PER_PAGE}
        perPageStorageKey={DEFAULT_PER_PAGE_STORAGE_KEY}
        page={1}
        downloadLinks={asyncDownloadLinks ? undefined : downloadLinks}
        asyncDownloadLinks={asyncDownloadLinks}
        RowEditor={RowEditor}
        actionCellRenderer={actionCellRenderer}
        showActions={showActions}
        showMetaCell={showMetaCell}
        renderCell={renderCell}
        onSaveCell={onSaveCell}
        onMenuAction={onMenuAction}
        viewMode={cardOnly ? "card" : viewMode}
        onViewModeChange={cardOnly ? undefined : onViewModeChange}
        renderCard={renderCard}
        // The leading card only belongs in card view; in list mode it would ship
        // as a stray first row. Gate it on the effective view mode.
        leadingCard={(cardOnly ? "card" : viewMode) === "card" ? leadingCard : undefined}
        onSearch={onSearch}
        searchPlaceholder={searchPlaceholder}
        toolbarActions={toolbarActions}
        enableRowSelection={hasBulkActions}
        bulkActionLinks={bulkActionLinks}
        onSelectionStateChange={(state) =>
          setHasSelection(
            state.mode === "explicit"
              ? state.selectedIds.length > 0
              : state.totalCount - state.excludedIds.length > 0
          )
        }
        showBulkActionMenu={hasBulkActions && hasSelection}
      />

      <UpdateStatusModal
        isOpen={isUpdateStatusOpen}
        onClose={() => setIsUpdateStatusOpen(false)}
        selectionCount={selectionCount}
        statusOptions={STATUS_OPTIONS}
        statusContentMap={buildStatusContentMap(selectionCount, preflight?.counts, emailOption, setEmailOption)}
        onConfirm={handleUpdateStatus}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteConfirmValue("");
        }}
        selectionCount={selectionCount}
        onConfirm={isDeleteConfirmValid && !nothingDeletable ? handleDelete : () => {}}
        rightButtonDisabled={!isDeleteConfirmValid || nothingDeletable}
      >
        {isInvitation ? (
          <InvitationDeleteContent
            selectionCount={selectionCount}
            preflightCounts={preflight?.counts}
            confirmValue={deleteConfirmValue}
            onConfirmValueChange={setDeleteConfirmValue}
            requiredConfirmText={requiredDeleteConfirmText}
          />
        ) : (
          <DeleteContent
            selectionCount={selectionCount}
            preflightCounts={preflight?.counts}
            confirmValue={deleteConfirmValue}
            onConfirmValueChange={setDeleteConfirmValue}
            requiredConfirmText={requiredDeleteConfirmText}
          />
        )}
      </DeleteModal>

      <ResendConfirmationModal
        isOpen={isResendConfirmationOpen}
        onClose={() => setIsResendConfirmationOpen(false)}
        onConfirm={handleResendConfirmation}
        selectionCount={selectionCount}
        preflightCounts={preflight?.counts}
      />

      <InvitationSendModal
        isOpen={isSendOpen}
        onClose={() => setIsSendOpen(false)}
        onConfirm={handleSend}
        selectionCount={selectionCount}
        preflightCounts={preflight?.counts}
      />

      <ArchiveConfirmationModal
        isOpen={isArchiveOpen}
        onClose={closeArchive}
        onConfirm={handleArchive}
        selectionCount={selectionCount}
        result={archiveResult}
        isSubmitting={isArchiving}
      />

      <RestoreConfirmationModal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        onConfirm={handleRestore}
        selectionCount={selectionCount}
        preflightCounts={preflight?.counts}
        sendEmail={restoreSendEmail}
        onSendEmailChange={setRestoreSendEmail}
      />
    </>
  );
};
